import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy } from "lucide-react";
import TopBar from "@/components/TopBar";
import NavPane from "@/components/NavPane";
import { fifteenApi } from "@/lib/fifteenApi";
import { playFifteenSlide, playFifteenWin } from "@/lib/sounds";

import { useEerieBackdrop } from "@/lib/eerie";

const ACCENT = "#B91C1C";       // blood red — used SPARINGLY for sub-canvas borders / leaderboard top
const RED_GLOW = "#DC2626";
const GOLD = "#D4AF37";          // gold — used ONLY for the 4×4 grid borders (per user)
const INK = "#F4F4F5";          // off-white — second dominant
const MUTED = "#7E7E80";
const DARK = "#0A0A0A";          // near-black — most dominant
const TILE_BG =
  "linear-gradient(160deg, #1F1F1F 0%, #0E0E0E 60%, #050505 100%)";
const TILE_ACTIVE =
  "linear-gradient(160deg, #FAFAFA 0%, #D4D4D4 60%, #71717A 100%)";

const SIZE = 4;
const GOAL = [...Array(15).keys()].map((i) => i + 1).concat([0]); // 1..15, 0 = empty

function isSolvable(arr) {
  // Standard 4×4 15-puzzle solvability: count inversions; if blank is on
  // an even row counting from the bottom, puzzle is solvable when
  // inversions is odd; otherwise inversions must be even.
  const tiles = arr.filter((x) => x !== 0);
  let inv = 0;
  for (let i = 0; i < tiles.length; i++)
    for (let j = i + 1; j < tiles.length; j++)
      if (tiles[i] > tiles[j]) inv++;
  const blankIdx = arr.indexOf(0);
  const blankRowFromBottom = SIZE - Math.floor(blankIdx / SIZE);
  if (blankRowFromBottom % 2 === 0) return inv % 2 === 1;
  return inv % 2 === 0;
}

function shuffleSolvable() {
  const arr = [...GOAL];
  // Do many random valid moves to guarantee solvability and avoid trivial states
  let blank = SIZE * SIZE - 1;
  for (let i = 0; i < 240; i++) {
    const neighbors = neighborsOf(blank);
    const target = neighbors[Math.floor(Math.random() * neighbors.length)];
    [arr[blank], arr[target]] = [arr[target], arr[blank]];
    blank = target;
  }
  // Safety: avoid an already-solved start
  if (arr.every((v, i) => v === GOAL[i])) return shuffleSolvable();
  if (!isSolvable(arr)) return shuffleSolvable();
  return arr;
}

function neighborsOf(i) {
  const r = Math.floor(i / SIZE);
  const c = i % SIZE;
  const out = [];
  if (r > 0)        out.push(i - SIZE);
  if (r < SIZE - 1) out.push(i + SIZE);
  if (c > 0)        out.push(i - 1);
  if (c < SIZE - 1) out.push(i + 1);
  return out;
}

function fmtTime(ms) {
  if (!ms || ms < 0) ms = 0;
  const totalSec = Math.floor(ms / 1000);
  const cs = Math.floor((ms % 1000) / 10);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${String(cs).padStart(2, "0")}`;
}

export default function FifteenHub() {
  const navigate = useNavigate();
  // Auto-scramble on every visit — the Hellraiser puzzle box is never at rest.
  const [tiles, setTiles] = useState(() => shuffleSolvable());
  const [moves, setMoves] = useState(0);
  const [startedAt, setStartedAt] = useState(null);
  const [endedAt, setEndedAt] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [name, setName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitMsg, setSubmitMsg] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loadingBoard, setLoadingBoard] = useState(false);
  // Iter 123: burst-glow flag held high for ~4.5s after solve, then released.
  const [winBurst, setWinBurst] = useState(false);
  const tickRef = useRef(0);

  // Phantasmic ambient backdrop (reverbed swooshes + ghostly bells)
  useEerieBackdrop("phantasm");

  const solved = useMemo(() => tiles.every((v, i) => v === GOAL[i]) && startedAt, [tiles, startedAt]);

  const elapsed = endedAt ? endedAt - startedAt : (startedAt ? now - startedAt : 0);

  // Tick clock while running
  useEffect(() => {
    if (!startedAt || endedAt) return;
    tickRef.current = setInterval(() => setNow(Date.now()), 80);
    return () => clearInterval(tickRef.current);
  }, [startedAt, endedAt]);

  // Auto-stop on solve
  useEffect(() => {
    if (solved && !endedAt) setEndedAt(Date.now());
  }, [solved, endedAt]);

  // Iter 123: when the puzzle is solved, fire the eerie 4.5s win
  // soundscape and trigger the bright glow burst on the numbers, then
  // let the burst fade out and the normal glow resume.
  useEffect(() => {
    if (!solved) return;
    try { playFifteenWin(); } catch { /* */ }
    setWinBurst(true);
    const t = setTimeout(() => setWinBurst(false), 4500);
    return () => clearTimeout(t);
  }, [solved]);

  // Initial leaderboard fetch
  const refreshBoard = async () => {
    setLoadingBoard(true);
    try { setLeaderboard(await fifteenApi.leaderboard(10)); }
    catch { /* ignore */ }
    finally { setLoadingBoard(false); }
  };
  useEffect(() => { refreshBoard(); }, []);

  // ---------------------------------------------------------------- actions
  const newGame = () => {
    setTiles(shuffleSolvable());
    setMoves(0);
    setStartedAt(null);
    setEndedAt(null);
    setSubmitted(false);
    setSubmitMsg(null);
    setWinBurst(false);
  };

  const click = (idx) => {
    if (endedAt) return;
    const blank = tiles.indexOf(0);
    const ns = neighborsOf(blank);
    if (!ns.includes(idx)) return;
    const next = tiles.slice();
    [next[blank], next[idx]] = [next[idx], next[blank]];
    setTiles(next);
    setMoves((m) => m + 1);
    if (!startedAt) setStartedAt(Date.now());
    try { playFifteenSlide(); } catch { /* */ }
  };

  // Keyboard arrow support — moves the tile that would slide INTO the blank
  useEffect(() => {
    const onKey = (e) => {
      if (endedAt) return;
      const blank = tiles.indexOf(0);
      const r = Math.floor(blank / SIZE), c = blank % SIZE;
      let target = -1;
      if (e.key === "ArrowUp"    && r < SIZE - 1) target = blank + SIZE;
      if (e.key === "ArrowDown"  && r > 0)        target = blank - SIZE;
      if (e.key === "ArrowLeft"  && c < SIZE - 1) target = blank + 1;
      if (e.key === "ArrowRight" && c > 0)        target = blank - 1;
      if (target >= 0) { e.preventDefault(); click(target); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tiles, endedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!solved || submitted) return;
    const trimmed = name.trim().slice(0, 20);
    if (!trimmed) { setSubmitMsg("Enter a name first"); return; }
    try {
      const myDuration = Math.round(endedAt - startedAt);
      await fifteenApi.submit({
        name: trimmed,
        duration_ms: myDuration,
        moves,
      });
      setSubmitted(true);
      // Refetch and tell the player whether they cracked the top 10.
      const fresh = await fifteenApi.leaderboard(10);
      setLeaderboard(fresh);
      const myRank = fresh.findIndex(
        (r) => r.name === trimmed && r.duration_ms === myDuration && r.moves === moves
      );
      if (myRank === 0) {
        setSubmitMsg("You took the crown — fastest solve in the book.");
      } else if (myRank > 0) {
        setSubmitMsg(`Inscribed at rank #${myRank + 1}.`);
      } else {
        setSubmitMsg("Logged — but you did not crack the top 10.");
      }
    } catch (e) {
      setSubmitMsg(e?.response?.data?.detail || e?.message || "Could not submit.");
    }
  };

  // ---------------------------------------------------------------- render
  return (
    <div
      className="relative min-h-screen overflow-x-clip"
      data-testid="fifteen-page"
      style={{
        background:
          // pitch black with subtle blood-red vignettes in opposite corners
          "radial-gradient(ellipse 30% 25% at 8% 8%, rgba(185,28,28,0.18), transparent 60%), radial-gradient(ellipse 35% 30% at 92% 92%, rgba(185,28,28,0.14), transparent 65%), radial-gradient(ellipse 80% 60% at 50% 50%, #0F0F0F 0%, #050505 70%, #000000 100%)",
        color: INK,
        fontFamily: "'Inter','Helvetica Neue',sans-serif",
      }}
    >
      {/* Hellraiser puzzle-box engraving overlay (subtle, very low opacity) */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240' viewBox='0 0 240 240'><g fill='none' stroke='%23ffffff' stroke-width='0.6'><rect x='30' y='30' width='180' height='180'/><rect x='60' y='60' width='120' height='120'/><rect x='90' y='90' width='60' height='60'/><line x1='30' y1='30' x2='210' y2='210'/><line x1='30' y1='210' x2='210' y2='30'/><circle cx='120' cy='120' r='12'/></g></svg>\")",
          backgroundSize: "240px 240px",
        }}
      />

      <TopBar
        theme="studio"
        onBack={() => navigate("/")}
        backLabel="HOME"
        testId="fifteen-back-button"
      />

      <div className="relative z-10 max-w-7xl mx-auto flex flex-col md:flex-row">
        <div
          className="md:w-[260px] md:shrink-0 md:min-h-screen flex flex-col pt-4 md:pt-6 px-4 md:px-6 pb-6 md:border-r"
          style={{ borderColor: "rgba(244,244,245,0.12)" }}
        >
          <NavPane theme="studio" />
        </div>
        <main className="flex-1 px-6 md:px-12 pt-8 pb-16">
          <p
            className="mb-6 text-sm md:text-base max-w-3xl"
            style={{ color: MUTED, fontFamily: "'Playfair Display','Georgia',serif", fontStyle: "italic", lineHeight: 1.55 }}
            data-testid="fifteen-blurb"
          >
            Emergent here. Andrew asked me for a haunted brass sliding-tile puzzle with a leaderboard
            whose score rewards both speed and economy of moves &mdash; I built the solvability engine,
            the gilded mist UI, the rock-moving sound, and the Mongo-backed Top 10 that ranks by
            1,000,000 &divide; (moves &times; seconds).
          </p>
          <h1
            className="mb-2"
            style={{
              fontFamily: "'Playfair Display','EB Garamond','Georgia',serif",
              fontWeight: 400,
              color: INK,
              fontSize: "clamp(2.25rem, 4.5vw, 3.75rem)",
              letterSpacing: "0.005em",
              textShadow: `0 0 24px rgba(220,38,38,0.25), 1px 1px 0 ${DARK}`,
              lineHeight: 1.05,
            }}
            data-testid="fifteen-title"
          >
            15-Tile Puzzle
          </h1>
          <p
            className="italic mb-8"
            style={{ color: MUTED, fontFamily: "'Playfair Display','Georgia',serif" }}
            data-testid="fifteen-subtitle"
          >
            Slide the squares 1 → 15. The clock starts on your first move.
          </p>

          <div className="flex flex-col gap-10" style={{ maxWidth: 720 }}>
            {/* BOARD COLUMN */}
            <div>
              {/* HUD */}
              <div className="flex items-center justify-between mb-3 max-w-[420px]">
                <div data-testid="fifteen-timer"
                     style={{
                       color: INK,
                       letterSpacing: "0.25em",
                       textShadow: `0 0 12px ${RED_GLOW}55`,
                       fontFamily: "'Times New Roman', Times, serif",
                       fontWeight: 700,
                       fontSize: "clamp(1.6rem, 2.4vw, 1.95rem)",
                       textTransform: "none",
                     }}>
                  {fmtTime(elapsed)}
                </div>
                <div className="text-sm" style={{ color: MUTED, fontFamily: "'Times New Roman', Times, serif" }} data-testid="fifteen-moves">
                  {moves} {moves === 1 ? "move" : "moves"}
                </div>
              </div>

              {/* Board — black box with thin GOLD rim, wrapped in a
                  scintillating bluish-white halo + outer haze ring. */}
              <div
                className="fifteen-board-halo relative mb-4"
                style={{
                  width: 420,
                  maxWidth: "100%",
                  padding: 18,
                }}
              >
                <div
                  className="grid gap-1.5 p-2 rounded-sm border-2"
                  style={{
                    gridTemplateColumns: `repeat(${SIZE}, 1fr)`,
                    borderColor: GOLD,
                    background: "linear-gradient(180deg, #0A0A0A 0%, #050505 100%)",
                    boxShadow: `inset 0 0 0 1px rgba(244,244,245,0.08), 0 0 18px rgba(212,175,55,0.22), 0 0 28px rgba(180,220,255,0.45), 0 0 60px rgba(140,200,255,0.35), 0 6px 36px rgba(0,0,0,0.85)`,
                    position: "relative",
                    zIndex: 1,
                  }}
                  data-testid="fifteen-board"
                >
                {tiles.map((v, i) => {
                  const blank = v === 0;
                  const correctlyPlaced = !blank && v === i + 1;
                  return (
                    <button
                      key={i}
                      disabled={blank || !!endedAt}
                      onClick={() => click(i)}
                      data-testid={`fifteen-tile-${i}`}
                      data-value={v}
                      data-silent="true"
                      className="aspect-square font-bold transition-transform select-none fifteen-tile-btn"
                      style={{
                        background: blank ? "rgba(0,0,0,0.85)" : TILE_BG,
                        // Solid saturated red — no gradient.  Brightness/
                        // glow comes from the pulsing CSS animation +
                        // text-shadow stack inside .fifteen-tile-num.
                        // Iter 123: keep the dark tile + red glow even
                        // after solve so the win-burst animation reads
                        // cleanly against the same baseline glow.
                        color: blank ? "transparent" : "#FF1F1F",
                        border: blank ? `1px dashed rgba(212,175,55,0.32)` : `1px solid ${GOLD}`,
                        boxShadow: blank
                          ? "none"
                          : (correctlyPlaced
                              ? `inset 0 0 0 1px rgba(212,175,55,0.6), 0 0 6px rgba(212,175,55,0.45)`
                              : `inset 0 0 0 1px rgba(212,175,55,0.18), 0 1px 4px rgba(0,0,0,0.65)`),
                        fontFamily: "'EB Garamond','Garamond','Adobe Garamond Pro',serif",
                        fontSize: "clamp(1.6rem, 4.5vw, 2.4rem)",
                        cursor: blank ? "default" : (endedAt ? "default" : "pointer"),
                        position: "relative",
                      }}
                    >
                      {blank ? "" : (
                        <span className="fifteen-tile-num" data-burst={winBurst ? "1" : "0"}>
                          {v}
                        </span>
                      )}
                    </button>
                  );
                })}
                </div>

                {/* Scintillating bluish-white halo — projected from the
                    grid's outer margins. Two layered radial pulses give
                    the impression of light "bleeding" off the puzzle. */}
                <div className="fifteen-halo-ring" aria-hidden="true" />
                <div className="fifteen-halo-haze" aria-hidden="true" />
                {/* Soft mist drifting around the gold rim (iter 53). */}
                <div className="fifteen-board-mist" aria-hidden="true" />
                {/* Four ornate corner flourishes — gold scrollwork
                    sitting just inside each corner of the frame. */}
                <div className="fifteen-frame-flourish" data-corner="tl" aria-hidden="true" />
                <div className="fifteen-frame-flourish" data-corner="tr" aria-hidden="true" />
                <div className="fifteen-frame-flourish" data-corner="bl" aria-hidden="true" />
                <div className="fifteen-frame-flourish" data-corner="br" aria-hidden="true" />
              </div>

              {/* Centered under the 420px-wide grid (mx-auto) so the
                  button's centre aligns with the grid's centre. */}
              <div className="flex flex-col items-center mb-6" style={{ width: 420, maxWidth: "100%" }}>
                <button
                  onClick={newGame}
                  data-testid="fifteen-new-game"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm tracking-[0.25em] uppercase"
                  style={{
                    background: "#5C1414",
                    color: INK,
                    fontWeight: 700,
                    border: `1px solid #5C1414`,
                    fontFamily: "'Times New Roman', Times, serif",
                  }}
                >
                  {startedAt ? "New Game" : "Scramble the Tablet"}
                </button>
                {solved && (
                  <div
                    className="text-sm font-bold mt-3"
                    style={{
                      color: RED_GLOW,
                      fontFamily: "Cinzel,'Playfair Display','EB Garamond',Georgia,serif",
                      fontWeight: 700,
                      letterSpacing: "0.25em",
                      textTransform: "none",
                    }}
                    data-testid="fifteen-solved-banner"
                  >
                    Congratulations.  You have solved this configuration in {fmtTime(elapsed)} · {moves} moves · score {Math.round(1_000_000 / (Math.max(1, moves) * Math.max(0.001, elapsed / 1000)))}
                  </div>
                )}
              </div>

              {/* Submit panel */}
              {solved && !submitted && (
                <div
                  className="p-4 rounded-sm border max-w-[420px]"
                  style={{ borderColor: ACCENT, background: "rgba(0,0,0,0.7)" }}
                  data-testid="fifteen-submit-panel"
                >
                  <div className="text-[10px] tracking-[0.3em] uppercase mb-2" style={{ color: ACCENT, fontFamily: "'Times New Roman', Times, serif" }}>
                    Inscribe your name
                  </div>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value.slice(0, 20))}
                    placeholder="Your name"
                    maxLength={20}
                    data-testid="fifteen-name-input"
                    className="w-full text-lg mb-3"
                    style={{
                      background: "transparent",
                      border: "none",
                      borderBottom: `1px solid ${INK}`,
                      color: INK,
                      outline: "none",
                      padding: "0.4rem 0",
                      fontFamily: "'Playfair Display','Georgia',serif",
                    }}
                  />
                  <button
                    onClick={submit}
                    data-testid="fifteen-submit-button"
                    className="px-4 py-2 text-sm tracking-[0.3em] uppercase"
                    style={{ background: INK, color: DARK, fontWeight: 700, border: `1px solid ${ACCENT}`, fontFamily: "'Times New Roman', Times, serif" }}
                  >
                    Submit Score
                  </button>
                  {submitMsg && (
                    <div
                      className="mt-2 text-xs"
                      style={{
                        color: MUTED,
                        fontFamily: "Cinzel,'Playfair Display','EB Garamond',Georgia,serif",
                        fontWeight: 700,
                        letterSpacing: "0.25em",
                        textTransform: "none",
                      }}
                      data-testid="fifteen-submit-msg"
                    >
                      {submitMsg}
                    </div>
                  )}
                </div>
              )}
              {submitted && (
                <div
                  className="text-sm"
                  style={{
                    color: RED_GLOW,
                    fontFamily: "Cinzel,'Playfair Display','EB Garamond',Georgia,serif",
                    fontWeight: 700,
                    letterSpacing: "0.25em",
                    textTransform: "none",
                  }}
                  data-testid="fifteen-submitted-msg"
                >
                  {submitMsg}
                </div>
              )}
            </div>

            {/* LEADERBOARD COLUMN */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] tracking-[0.4em] uppercase" style={{ color: ACCENT, fontFamily: "'Times New Roman', Times, serif" }}>
                  <Trophy size={12} className="inline -mt-0.5 mr-1" /> The Solvers · Top 10
                </div>
                <button
                  onClick={refreshBoard}
                  className="text-xs uppercase tracking-[0.3em]"
                  style={{ color: MUTED, fontFamily: "'Times New Roman', Times, serif" }}
                  data-testid="fifteen-refresh-leaderboard"
                >
                  Refresh
                </button>
              </div>
              <div
                className="rounded-sm border"
                style={{ borderColor: "rgba(244,244,245,0.18)", background: "rgba(0,0,0,0.55)" }}
                data-testid="fifteen-leaderboard"
              >
                {/* Column legend */}
                <div
                  className="grid grid-cols-[2rem,1fr,5rem,5rem,3.5rem] items-center gap-3 px-3 py-2 text-[10px] tracking-[0.3em] uppercase"
                  style={{ color: MUTED, borderBottom: "1px solid rgba(244,244,245,0.12)", fontFamily: "'Times New Roman', Times, serif" }}
                >
                  <div className="text-center">#</div>
                  <div>Solver</div>
                  <div className="text-right">Score</div>
                  <div className="text-right">Time</div>
                  <div className="text-right">Mv</div>
                </div>
                {loadingBoard && leaderboard.length === 0 && (
                  <div className="p-4 text-sm italic" style={{ color: MUTED }}>Loading…</div>
                )}
                {!loadingBoard && leaderboard.length === 0 && (
                  <div className="p-4 text-sm italic" style={{ color: MUTED }} data-testid="fifteen-leaderboard-empty">
                    No solvers yet. Be the first to inscribe your name.
                  </div>
                )}
                {leaderboard.map((row, i) => (
                  <div
                    key={`${row.name}-${row.completed_at}`}
                    className="grid grid-cols-[2rem,1fr,5rem,5rem,3.5rem] items-center gap-3 px-3 py-2 border-t"
                    style={{ borderColor: i === 0 ? "transparent" : "rgba(244,244,245,0.08)" }}
                    data-testid={`fifteen-row-${i}`}
                  >
                    <div
                      className="text-xs text-center"
                      style={{ color: i === 0 ? RED_GLOW : (i < 3 ? INK : MUTED), fontFamily: "'Times New Roman', Times, serif" }}
                    >
                      {i + 1}
                    </div>
                    <div className="text-sm truncate" style={{ fontFamily: "'Playfair Display',serif", color: INK }}>
                      {row.name}
                    </div>
                    <div
                      className="text-right text-sm font-bold"
                      style={{ color: i === 0 ? RED_GLOW : INK, fontFamily: "'Times New Roman', Times, serif", textShadow: i === 0 ? `0 0 8px ${RED_GLOW}66` : "none" }}
                      data-testid={`fifteen-row-${i}-score`}
                    >
                      {Math.round(Number(row.score ?? 0))}
                    </div>
                    <div className="text-right text-xs" style={{ color: MUTED, fontFamily: "'Times New Roman', Times, serif" }}>
                      {fmtTime(row.duration_ms)}
                    </div>
                    <div className="text-right text-xs" style={{ color: MUTED, fontFamily: "'Times New Roman', Times, serif" }}>
                      {row.moves}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
